import {
  CopyOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  LeftOutlined,
  MailOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { Button, Card, Flex, Row, Skeleton, Tabs, type TabsProps } from "antd";
import React, { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useBreakpointView, useIsMobileView } from "../../hooks/ui-hook";
import { getNotesDetails, updateNote } from "../../service/notes.service";
import { setCustomiseSectionState, setLoadingNotesState, setNotesViewVisible } from "../../slices/appStateSlice";
import { resetNotesDetails, setActiveKey, setNotesDetails } from "../../slices/notesSlice";
import { copyAllContent, downloadNotePDF, generateHTMLForPDF } from "../../utils/datamanipulation.utils";
import { eventEmitter } from "../../utils/eventEmitter";
import EmailView from "./EmailView.component";
import NotesHeader from "./NotesHeader.component";
import NotesNiew from "./NotesView.component";
import TranscriptView from "./TranscriptView.component";
import styles from "./notes.module.scss";

export default function Notes() {
  const componentName = "notes";
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileView();
  const isCompactView = useBreakpointView(1400);
  const { loadingNotes, isDemoPath, isMobilePath } = useAppSelector((state) => state.appState);
  const { notesDetails } = useAppSelector((state) => state.notes);
  const { userInfo } = useAppSelector((state) => state.auth);
  const [isScrolled, setIsScrolled] = React.useState(false);

  const markNoteAsRead = useCallback(
    async (recordingId: string | number) => {
      if (isDemoPath) return;
      const updateNoteData = {
        audio_id: recordingId,
        is_read: true,
      };
      await updateNote(updateNoteData)
        .then((res) => {
          dispatch(setNotesDetails(res.data));
        })
        .catch((error) => {
          console.error("Error marking note as read:", error);
        });
    },
    [dispatch, isDemoPath],
  );

  React.useEffect(() => {
    if (notesDetails?.id && !notesDetails.is_read) {
      markNoteAsRead(notesDetails.id);
      eventEmitter.emit("noteFirstRead", { id: notesDetails.id, client: notesDetails.client });
    }
  }, [markNoteAsRead, notesDetails?.id, notesDetails?.is_read]);

  React.useEffect(() => {
    const handleScroll = () => {
      const notesContainer = document.getElementById("notes-container");
      const notesContainerMobile = document.getElementById("notes-container-mobile");
      const container = isMobile ? notesContainerMobile : notesContainer;
      if (container) {
        const scrollTop = container.scrollTop;
        setIsScrolled(scrollTop > 10);
      }
    };

    const notesContainer = document.getElementById("notes-container");
    const notesContainerMobile = document.getElementById("notes-container-mobile");
    const container = isMobile ? notesContainerMobile : notesContainer;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [isMobile]);

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Note",
      children: <NotesNiew />,
      icon: <FileTextOutlined />,
    },
    {
      key: "2",
      label: "Email",
      children: <EmailView />,
      icon: <MailOutlined />,
    },
  ];

  if (userInfo.can_view_transcript) {
    items.push({
      key: "3",
      label: "Transcript",
      children: <TranscriptView />,
      icon: <FileTextOutlined />,
    });
  }

  const closeNote = () => {
    dispatch(setNotesViewVisible(false));
    dispatch(setNotesDetails(null));
    dispatch(setCustomiseSectionState(false));
    dispatch(resetNotesDetails());
  };

  const fetchRecordingDetails = async (recordingId: string | number) => {
    try {
      const response = await getNotesDetails(recordingId);
      const data = response.data;
      dispatch(setNotesDetails(data));
      dispatch(setLoadingNotesState(false));
    } catch (error) {
      dispatch(setLoadingNotesState(false));
      console.error("Error fetching recording details:", error);
    }
  };

  const handlePrint = () => {
    if (notesDetails) {
      const html = generateHTMLForPDF(notesDetails, userInfo?.practice_area);
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      }
    }
  };

  const handleDownloadPDF = () => {
    if (notesDetails) {
      downloadNotePDF(notesDetails, userInfo?.practice_area);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (notesDetails?.id && !isDemoPath) {
      fetchRecordingDetails(notesDetails.id);
    } else if (notesDetails && isDemoPath) {
      dispatch(setNotesDetails(notesDetails));
      dispatch(setLoadingNotesState(false));
    }
  }, [notesDetails?.id, dispatch, isDemoPath]);

  const handleCopyAllDetails = () => {
    if (notesDetails) {
      copyAllContent(notesDetails, userInfo?.practice_area);
    }
  };

  const CopyAllDetails = ({ className = "" }: { className?: string }) => {
    return (
      <Button
        className={`${styles[`${componentName}__sticky-header-parallel-button`]} ${className}`}
        icon={<CopyOutlined />}
        onClick={handleCopyAllDetails}
      >
        Copy All Details
      </Button>
    );
  };

  const PrintButton = () => {
    return (
      <Button
        className={`${styles[`${componentName}__sticky-header-parallel-button`]}`}
        icon={<PrinterOutlined />}
        onClick={handlePrint}
      >
        Print
      </Button>
    );
  };

  const DownloadPDFButton = () => {
    return (
      <Button
        className={`${styles[`${componentName}__sticky-header-parallel-button`]}`}
        icon={<FilePdfOutlined />}
        onClick={handleDownloadPDF}
      >
        Download PDF
      </Button>
    );
  };

  return (
    <Row className={`${styles[`${componentName}__main`]}`}>
      {loadingNotes && (
        <Flex vertical className={`${styles[`${componentName}__main-loading`]} w-full`}>
          <Skeleton active />
          <Skeleton active />
          <Skeleton active />
        </Flex>
      )}
      {!loadingNotes && notesDetails && (
        <>
          <Flex
            className={`${styles[`${componentName}__sticky-header`]} ${isScrolled ? styles.scrolled : ""}`}
            justify="space-between"
          >
            <Flex justify="space-between" className="w-full">
              <Button
                className={`${styles[`${componentName}-back`]} secondary-button`}
                onClick={closeNote}
                icon={<LeftOutlined />}
              >
                Back
              </Button>
              <Flex gap={10}>
                {!isMobilePath && (
                  <>
                    {!isCompactView && (
                      <CopyAllDetails className={`${styles[`${componentName}__sticky-header-mobile-button`]}`} />
                    )}
                    <PrintButton />
                    <DownloadPDFButton />
                  </>
                )}
              </Flex>
            </Flex>
            <Flex className={`${styles[`${componentName}__sticky-header-mobile`]} w-full`}>
              <CopyAllDetails className={"w-full"} />
            </Flex>
          </Flex>
          <Flex
            vertical
            gap={10}
            className={styles[`${componentName}__notes-detail`]}
            style={{
              width: "100%",
            }}
          >
            <Card>
              <NotesHeader notesDetails={notesDetails} />
            </Card>
            <Tabs
              className={styles[`${componentName}__tabs`]}
              onChange={(activeKey) => {
                dispatch(setActiveKey(activeKey));
              }}
              items={items}
            />
          </Flex>
        </>
      )}
    </Row>
  );
}
